import { SubmissionToolHeader } from '@/components/SubmissionToolHeader';
import {
  useEffect,
  useCallback,
  useState,
  useRef,
  type ChangeEvent,
  type DragEvent,
  type FormEvent,
} from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { v2, ApiError, type Document } from '@/api/v2Client';
import { CTD_MODULES, LIFECYCLE_OPERATIONS } from '@/constants/ctd';
import { formatBytes } from '@/lib/format';
import { extractBodyText, formatApiError } from '@/lib/errors';
import { SignatureStatusPill } from '@/components/submission/SignatureStatusPill';
import { useSubmissionDraft } from '@/hooks/useSubmissionWorkspace';
import { useUserPermissions } from '@/hooks/useUserPermissions';

const UPLOAD_ERROR_MESSAGES: Readonly<Record<string, string>> = {
  PermissionDenied: 'Your role does not have permission to upload documents.',
  NotFound: 'This submission is no longer available or you do not have access.',
  DatabaseUnavailable: 'The database is temporarily unavailable. Please try again.',
};

/** Known upload refusals stay readable even when the server supplies technical detail. */
function formatUploadError(error: ApiError, fallback: string): string {
  const { code } = extractBodyText(error);
  return (code ? UPLOAD_ERROR_MESSAGES[code] : undefined) ?? formatApiError(error, fallback);
}

// TODO-069 — accepted MIME types / extensions (mirrors the per-row `accept`
// attribute from TODO-067). Files dropped that don't match are silently
// filtered so the queue stays clean.
const ACCEPTED_EXTENSIONS = ['.pdf', '.xml', '.xsl', '.dtd', '.txt', '.doc', '.docx'];

function isAcceptedFile(f: File): boolean {
  const name = f.name.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
}

interface UploadRow {
  fileName: string;
  moduleNumber: string;
  lifecycleOperation: 'new' | 'replace' | 'append' | 'delete';
  file: File | null;
  // TODO-074 — added 'auto-classifying' between 'done' (upload complete)
  // and the final visible state. The cell renders a spinner-style label
  // while the backend's fire-and-forget classifier completes.
  status:
    | 'idle'
    | 'creating'
    | 'uploading'
    | 'auto-classifying'
    | 'done'
    | 'error';
  error?: string | undefined;
  /**
   * T-17 (T17-D5) — the server-side PENDING record `create` made for this row.
   * It becomes a real document only when the upload succeeds, so while the row
   * is in `error` it is reused by the retry (never re-created) and dropped via
   * `abandonPendingUpload` if the row is removed or the page is left.
   */
  documentId?: string | undefined;
  /** The file name / module / operation `documentId` was created with. If the
   *  user edits any of them before retrying, the pending record is stale. */
  createdKey?: string | undefined;
  /** TODO-074 — last classification observed from polling (for the cell label). */
  classification?: string | null;
  /** Phase upload-progress-bar — 0..100 while status==='uploading'. null
   *  means progress events aren't available (XHR unsupported) and the cell
   *  should fall back to the indeterminate "Uploading…" label. */
  uploadProgress?: number | null;
}

// TODO-074 — Settings polling for the per-row auto-classify indicator.
// We poll v2.documents.get(documentId) every POLL_INTERVAL_MS up to
// MAX_POLL_MS; when Classification flips non-null OR the timeout
// elapses, we stop and resolve the row to 'done'. Polling is
// intentionally short (10s total) because the indicator is a UX hint,
// not a guarantee — the backend can still finish in the background.
const POLL_INTERVAL_MS = 1500;
const MAX_POLL_MS = 10_000;

function emptyRow(): UploadRow {
  return {
    fileName: '',
    moduleNumber: '1',
    lifecycleOperation: 'new',
    file: null,
    status: 'idle',
  };
}

export function UploadDocsPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const permissions = useUserPermissions();
  const canUpload = !permissions.loading && !permissions.failed && permissions.hasPermission('Documents', 'write');
  const [existingError, setExistingError] = useState<string | null>(null);
  const [fileFeedback, setFileFeedback] = useState('');
  const [existing, setExisting] = useState<Document[] | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [rows, setRows] = useState<UploadRow[]>([emptyRow()]);
  const [busy, setBusy] = useState(false);
  // TODO-069 — drag-drop highlight state (mirrors DocumentsTabPanel pattern).
  const [dragging, setDragging] = useState(false);
  // TODO-074 — effective `autoClassifyOnUpload` value. Fetched once on
  // mount. Default true matches the schema default so the indicator renders
  // even if the fetch fails (which is the user's intent — they expect
  // auto-classify to be on by default).
  const [autoClassifyEnabled, setAutoClassifyEnabled] = useState<boolean | null>(null);
  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  useEffect(() => {
    if (!id) return;
    let stale = false;
    setExistingError(null);
    v2.documents.listBySubmission(id).then((docs) => { if (!stale) setExisting(docs); })
      .catch((err: unknown) => { if (!stale) setExistingError(err instanceof ApiError ? formatApiError(err, 'Could not load existing documents.', UPLOAD_ERROR_MESSAGES) : 'Could not load existing documents.'); });
    return () => { stale = true; };
  }, [id, reloadKey]);

  // TODO-074 — read the autoClassifyOnUpload setting once on mount.
  // T-17 (D6): for THIS submission — the submission-merged read (submission >
  // user > org > default, resolved server-side by the one settings resolver),
  // so a submission-level override shows the right indicator.
  useEffect(() => {
    if (!id) return;
    v2.settings
      .getSubmission(id)
      .then((res) => {
        const row = res.settings['autoClassifyOnUpload'];
        if (row && typeof row.Value === 'boolean') {
          setAutoClassifyEnabled(row.Value);
        }
        else setAutoClassifyEnabled(null);
      })
      .catch(() => {
        setAutoClassifyEnabled(null);
      });
  }, [id]);

  const handleAddRow = (): void => setRows((rs) => [...rs, emptyRow()]);
  // T-17 (T17-D5) — best-effort: tell the server to drop a pending upload record.
  // The server only removes a still-PENDING row created by this user (404 for
  // anything else, e.g. an upload that actually completed), and purges stale
  // pending rows itself, so a failed call is harmless and never surfaced.
  const abandonPending = (documentId: string): void => {
    void v2.documents.abandonPendingUpload(documentId).catch(() => undefined);
  };
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const discardUploads = useCallback(() => {
    for (const row of rowsRef.current) {
      if (row.documentId && row.status === 'error') {
        void v2.documents.abandonPendingUpload(row.documentId).catch(() => undefined);
      }
    }
    setRows([emptyRow()]);
    for (const input of Object.values(fileInputRefs.current)) {
      if (input) input.value = '';
    }
  }, []);
  const hasPendingUploads = rows.some((row) =>
    row.status !== 'done' && row.status !== 'auto-classifying' &&
    (row.file !== null || row.fileName.trim() !== ''),
  );
  useSubmissionDraft(hasPendingUploads || busy, discardUploads, busy);
  useEffect(
    () => () => {
      // leaving the page abandons rows whose upload failed (nothing else will)
      for (const r of rowsRef.current) {
        if (r.documentId && r.status === 'error') {
          void v2.documents.abandonPendingUpload(r.documentId).catch(() => undefined);
        }
      }
    },
    [],
  );
  const handleRemoveRow = (idx: number): void => {
    if (rows.length === 1) return;
    const removed = rows[idx];
    // A row whose upload completed holds a real document: never abandon it.
    if (removed?.documentId && removed.status !== 'done' && removed.status !== 'auto-classifying') {
      abandonPending(removed.documentId);
    }
    setRows((rs) => rs.filter((_, i) => i !== idx));
  };
  const updateRow = (idx: number, patch: Partial<UploadRow>): void =>
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  // TODO-069 — bulk-accept files from the dropzone. Each dropped file becomes
  // a new row in the upload table with FileName pre-filled from the file's
  // own name (matching the auto-populate behaviour of onFileSelect). Non-
  // accepted extensions are filtered out. If the first row is still empty
  // (the default starter row), the first dropped file is placed there
  // instead of appending — keeps the table tidy on the common single-drop case.
  const acceptFiles = (files: File[]): File[] => {
    const accepted = files.filter(isAcceptedFile);
    const rejected = files.filter((file) => !isAcceptedFile(file));
    setFileFeedback(`${accepted.length} file(s) accepted.${rejected.length ? ` ${rejected.length} rejected (unsupported type): ${rejected.map((file) => file.name).join(', ')}. Allowed: ${ACCEPTED_EXTENSIONS.join(', ')}.` : ''}`);
    return accepted;
  };
  const handleDroppedFiles = (files: File[]): void => {
    const accepted = acceptFiles(files);
    if (accepted.length === 0) return;
    setRows((rs) => {
      const next = [...rs];
      let cursor = 0;
      for (const f of accepted) {
        // Reuse an existing empty row before appending new ones.
        let placed = false;
        for (; cursor < next.length; cursor += 1) {
          const r = next[cursor];
          if (r && !r.file && r.fileName.trim() === '') {
            next[cursor] = { ...r, file: f, fileName: f.name };
            cursor += 1;
            placed = true;
            break;
          }
        }
        if (!placed) {
          next.push({ ...emptyRow(), file: f, fileName: f.name });
        }
      }
      return next;
    });
  };

  const onDragOver = (e: DragEvent<HTMLElement>): void => {
    e.preventDefault();
    setDragging(true);
  };
  const onDragLeave = (): void => setDragging(false);
  const onDrop = (e: DragEvent<HTMLElement>): void => {
    e.preventDefault();
    setDragging(false);
    const files = Array.from(e.dataTransfer.files);
    handleDroppedFiles(files);
  };

  const onFileSelect = (idx: number, e: ChangeEvent<HTMLInputElement>): void => {
    // phase-fix-f4-and-small-findings (U1): the input is now `multiple`. The
    // first selected file goes to the row whose input was used; any additional
    // files spill into empty rows after it (or append new ones), so selecting N
    // files creates N rows — each one still submits through the existing
    // per-file create/upload path, no bulk endpoint.
    const selected = acceptFiles(Array.from(e.target.files ?? []));
    if (selected.length === 0) {
      updateRow(idx, { file: null });
      return;
    }
    setRows((rs) => {
      const next = [...rs];
      const [first, ...rest] = selected;
      const target = next[idx];
      if (target && first) {
        next[idx] = {
          ...target,
          file: first,
          fileName: target.fileName.trim() ? target.fileName : first.name,
        };
      }
      // Remaining files: reuse an empty row after idx before appending.
      let cursor = idx + 1;
      for (const f of rest) {
        let placed = false;
        for (; cursor < next.length; cursor += 1) {
          const r = next[cursor];
          if (r && !r.file && r.fileName.trim() === '') {
            next[cursor] = { ...r, file: f, fileName: f.name };
            cursor += 1;
            placed = true;
            break;
          }
        }
        if (!placed) {
          next.push({ ...emptyRow(), file: f, fileName: f.name });
        }
      }
      return next;
    });
  };

  const submitAll = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (busy || !canUpload) return;
    setBusy(true);

    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      if (!r || !r.file || !r.fileName.trim()) continue;
      if (r.status === 'done') continue;

      updateRow(i, { status: 'creating', error: undefined });
      // T-17 (T17-D5) — create is a two-step protocol with the upload: the
      // record `create` makes is PENDING until the upload succeeds. A retry
      // after a failed upload reuses it (re-creating leaked one orphan record
      // per attempt). If the user changed what the record was created with,
      // the stale pending record is dropped and a fresh one is created.
      const createdKey = `${r.fileName.trim()}|${r.moduleNumber}|${r.lifecycleOperation}`;
      let documentId: string;
      if (r.documentId && r.createdKey === createdKey) {
        documentId = r.documentId;
      } else {
        if (r.documentId) {
          abandonPending(r.documentId);
          updateRow(i, { documentId: undefined, createdKey: undefined });
        }
        try {
          const doc: Document = await v2.documents.create({
            SubmissionId: id,
            FileName: r.fileName.trim(),
            ModuleNumber: r.moduleNumber,
            LifecycleOperation: r.lifecycleOperation,
          });
          documentId = doc.Id;
        } catch (err) {
          updateRow(i, {
            status: 'error',
            error: err instanceof ApiError ? formatUploadError(err, 'Failed to create document') : 'Failed to create document',
          });
          continue;
        }
      }
      const doc = { Id: documentId };

      updateRow(i, { status: 'uploading', documentId: doc.Id, createdKey, uploadProgress: 0 });
      try {
        // Ensure the uploaded File has the exact name expected by the backend
        // (multipart filename must match Document.FileName per Phase 3.2).
        const matchedFile =
          r.file.name === r.fileName.trim()
            ? r.file
            : new File([r.file], r.fileName.trim(), { type: r.file.type });
        // Phase upload-progress-bar — drive the per-row progress bar from
        // XHR upload events. Falls back to plain fetch (no progress) when
        // XMLHttpRequest is unavailable; in that case updateRow never
        // fires and the cell shows the indeterminate "Uploading…" label.
        await v2.documents.uploadFileWithProgress(doc.Id, matchedFile, (pct) => {
          updateRow(i, { uploadProgress: pct });
        });
        // TODO-074 — if the user has autoClassifyOnUpload on, surface an
        // "Auto-classifying..." indicator while the backend's fire-and-
        // forget classifier runs. Otherwise show 'done' immediately.
        if (autoClassifyEnabled) {
          updateRow(i, { status: 'auto-classifying' });
          void pollForClassification(i, doc.Id);
        } else {
          updateRow(i, { status: 'done' });
        }
      } catch (err) {
        updateRow(i, {
          status: 'error',
          error: err instanceof ApiError ? formatUploadError(err, 'Failed to upload file') : 'Failed to upload file',
        });
      }
    }

    setBusy(false);
    setReloadKey((k) => k + 1);
  };

  // TODO-074 — polls v2.documents.get(documentId) until either:
  //   • a Classification value appears (transition to 'done'), or
  //   • MAX_POLL_MS elapses (also transition to 'done' — the indicator is
  //     informational; the backend may still complete later).
  // Errors from individual polls are swallowed so a transient failure
  // doesn't strand the row at 'auto-classifying' forever.
  const pollForClassification = async (
    rowIdx: number,
    documentId: string,
  ): Promise<void> => {
    const started = Date.now();
    while (Date.now() - started < MAX_POLL_MS) {
      await new Promise((resolve) => globalThis.setTimeout(resolve, POLL_INTERVAL_MS));
      try {
        const doc = await v2.documents.get(documentId);
        if (doc.Classification) {
          updateRow(rowIdx, { status: 'done', classification: doc.Classification });
          return;
        }
      } catch {
        // ignore transient errors and keep polling
      }
    }
    // Timeout — the classifier may still be running, but we stop showing
    // the indicator so the user isn't blocked visually.
    updateRow(rowIdx, { status: 'done' });
  };

  return (
    <section>
      {id && <SubmissionToolHeader id={id} />}
      {existingError && <div role="alert">{existingError} <button type="button" onClick={() => setReloadKey((key) => key + 1)}>Retry document list</button></div>}
      {fileFeedback && <p role="status">{fileFeedback}</p>}
      {canUpload && autoClassifyEnabled === null && <p role="note">Automatic classification setting is unavailable. Upload success does not confirm classification; review documents after upload.</p>}
      <Link to={`/submissions/${id}`} style={{ color: '#1B3A6B' }}>
        ← Back to submission
      </Link>
      {/* phase-v1-ui-parity — V1 UploadDocsScreen: "Upload Documents" via
          .screen-title. */}
      <h2 className="screen-title" style={{ margin: '12px 0 6px 0' }}>Upload Documents</h2>

      {!canUpload && <p role="status">{permissions.loading
        ? 'Checking upload permissions…'
        : permissions.failed
          ? <>Could not check upload permissions. <button type="button" onClick={() => void permissions.refresh()}>Retry permissions</button></>
          : 'Your role does not have permission to upload documents.'}</p>}
      {canUpload && <>

      {/* TODO-069 — full-width drag-drop zone above the structured form. Mirrors
          DocumentsTabPanel's `.dropzone` pattern; dropped files populate the
          row table below (one row per file) so the user still confirms module
          + lifecycle operation per file before submitting. */}
      <div
        className={`dropzone${dragging ? ' drag-over' : ''}`}
        role="region"
        aria-label="Drag-drop zone for document upload"
        data-testid="upload-page-dropzone"
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        style={{ marginBottom: 16 }}
      >
        <div className="dropzone-icon" aria-hidden="true">
          ⬆
        </div>
        <p>
          {dragging ? (
            <strong>Drop to upload</strong>
          ) : (
            <>
              <strong>Drag PDF files here</strong>, or use the form below
            </>
          )}
        </p>
        <small>PDF / DOCX / XML / XSL / DTD / TXT — dropped files appear as rows below</small>
      </div>

      <form onSubmit={submitAll}>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #e5e7eb', textAlign: 'left' }}>
              <th style={{ padding: 8 }}>File</th>
              <th style={{ padding: 8 }}>File name</th>
              <th style={{ padding: 8 }}>Module</th>
              <th style={{ padding: 8 }}>Operation</th>
              <th style={{ padding: 8 }}>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: 8 }}>
                  <input
                    aria-label={`File for upload row ${idx + 1}`}
                    type="file"
                    multiple
                    accept="application/pdf,.pdf,.xml,.xsl,.dtd,.txt,.doc,.docx"
                    ref={(el) => {
                      fileInputRefs.current[idx] = el;
                    }}
                    onChange={(e) => onFileSelect(idx, e)}
                  />
                </td>
                <td style={{ padding: 8 }}>
                  <input
                    aria-label={`File name for upload row ${idx + 1}`}
                    value={r.fileName}
                    onChange={(e) => updateRow(idx, { fileName: e.target.value })}
                    placeholder="e.g. cover-letter.pdf"
                    style={{ width: '100%', padding: 6, border: '1px solid #d1d5db', borderRadius: 4 }}
                  />
                </td>
                <td style={{ padding: 8 }}>
                  <select
                    aria-label={`Module for upload row ${idx + 1}`}
                    value={r.moduleNumber}
                    onChange={(e) => updateRow(idx, { moduleNumber: e.target.value })}
                  >
                    {CTD_MODULES.map((m) => (
                      <option key={m} value={m}>
                        m{m}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={{ padding: 8 }}>
                  <select
                    aria-label={`Operation for upload row ${idx + 1}`}
                    value={r.lifecycleOperation}
                    onChange={(e) =>
                      updateRow(idx, {
                        lifecycleOperation: e.target.value as UploadRow['lifecycleOperation'],
                      })
                    }
                  >
                    {LIFECYCLE_OPERATIONS.map((op) => (
                      <option key={op} value={op}>
                        {op}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={{ padding: 8, fontSize: 13 }}>
                  {r.status === 'idle' && '—'}
                  {r.status === 'creating' && 'Creating…'}
                  {r.status === 'uploading' &&
                    (typeof r.uploadProgress === 'number' ? (
                      <div
                        data-testid={`upload-progress-${idx}`}
                        role="progressbar"
                        aria-valuenow={r.uploadProgress}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label={`Upload progress for ${r.fileName}`}
                        style={{
                          position: 'relative',
                          width: 140,
                          height: 16,
                          background: '#e5e7eb',
                          borderRadius: 8,
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          data-testid={`upload-progress-bar-${idx}`}
                          style={{
                            width: `${r.uploadProgress}%`,
                            height: '100%',
                            background: '#2563EB',
                            transition: 'width 120ms linear',
                          }}
                        />
                        <span
                          data-testid={`upload-progress-label-${idx}`}
                          style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 11,
                            fontWeight: 600,
                            color: r.uploadProgress >= 55 ? '#fff' : '#1f2937',
                          }}
                        >
                          Uploading… {r.uploadProgress}%
                        </span>
                      </div>
                    ) : (
                      'Uploading…'
                    ))}
                  {/* TODO-074 — auto-classify indicator (visible only when the
                      tenant/user has autoClassifyOnUpload enabled). */}
                  {r.status === 'auto-classifying' && (
                    <span
                      style={{ color: '#1B3A6B', fontStyle: 'italic' }}
                      data-testid={`upload-row-classifying-${idx}`}
                    >
                      ⟳ Auto-classifying…
                    </span>
                  )}
                  {r.status === 'done' && (
                    <span style={{ color: '#15803d' }}>
                      ✓ Uploaded{!r.classification && autoClassifyEnabled !== false ? ' · Classification not confirmed' : ''}
                      {r.classification && (
                        <span
                          style={{ opacity: 0.75, fontWeight: 400, marginLeft: 6 }}
                          data-testid={`upload-row-classification-${idx}`}
                        >
                          · {r.classification}
                        </span>
                      )}
                    </span>
                  )}
                  {r.status === 'error' && <span style={{ color: '#b91c1c' }}>✕ {r.error}</span>}
                </td>
                <td style={{ padding: 8 }}>
                  {rows.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveRow(idx)}
                      style={{ color: '#b91c1c', background: 'none', border: 'none', cursor: 'pointer' }}
                    >
                      ×
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
          <button
            type="button"
            onClick={handleAddRow}
            style={{ padding: '6px 12px', background: '#fff', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer' }}
          >
            + Add row
          </button>
          <button
            type="submit"
            disabled={busy}
            style={{
              padding: '8px 16px',
              background: '#1B3A6B',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontWeight: 600,
              cursor: busy ? 'not-allowed' : 'pointer',
              opacity: busy ? 0.6 : 1,
            }}
          >
            {busy ? 'Uploading…' : 'Upload all'}
          </button>
        </div>
      </form>
      </>}

      {/* Phase security-housekeeping (TODO-082) — proceed-to-classification
          CTA. Appears once at least one row has finished uploading; clicking
          jumps the publisher back to the Submission Details page where the
          newly-uploaded docs are classified and confirmed. */}
      {rows.some((r) => r.status === 'done') && (
        <div
          data-testid="upload-success-cta"
          style={{
            margin: '16px 0',
            padding: '12px 16px',
            background: '#f0fdf4',
            border: '1px solid #86efac',
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <span style={{ color: '#15803d', fontSize: 14 }}>
            ✓ Upload complete. Proceed to classify the new documents.
          </span>
          <button
            type="button"
            data-testid="upload-success-back-button"
            onClick={() => navigate(`/submissions/${id}`)}
            style={{
              padding: '8px 14px',
              background: '#1B3A6B',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            ← Back to Submission Details
          </button>
        </div>
      )}

      <h3>Existing documents</h3>
      {existing === null && !existingError && <p>Loading…</p>}
      {existing && existing.length === 0 && (
        <p style={{ color: '#6b7280' }}>No documents yet.</p>
      )}
      {existing && existing.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
              <th style={{ padding: 8 }}>File</th>
              <th style={{ padding: 8 }}>Module</th>
              <th style={{ padding: 8 }}>Op</th>
              <th style={{ padding: 8 }}>Uploaded</th>
              <th style={{ padding: 8 }}>Signature</th>
              <th style={{ padding: 8 }}>Size</th>
            </tr>
          </thead>
          <tbody>
            {existing.map((d) => (
              <tr key={d.Id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: 8 }}>{d.FileName}</td>
                <td style={{ padding: 8 }}>{d.ModuleNumber ?? '—'}</td>
                <td style={{ padding: 8 }}>{d.LifecycleOperation}</td>
                <td style={{ padding: 8 }}>
                  {d.Sha256Hash ? <span style={{ color: '#15803d' }}>✓</span> : '—'}
                </td>
                <td style={{ padding: 8 }}>
                  <SignatureStatusPill status={d.SignatureStatus} />
                </td>
                <td style={{ padding: 8 }}>{formatBytes(d.FileSize)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
